import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Minus, X } from 'lucide-react'

export interface InboxActiveFilter {
  id: string
  label: string
  onRemove: () => void
  keepVisible?: boolean
}

interface InboxActiveFiltersProps {
  filters: InboxActiveFilter[]
  onClearAll: () => void
  maxVisible?: number
}

export function InboxActiveFilters({
  filters,
  onClearAll,
  maxVisible = 4,
}: InboxActiveFiltersProps) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  if (filters.length === 0) return null

  const pinned = filters.filter((filter) => filter.keepVisible)
  const unpinned = filters.filter((filter) => !filter.keepVisible)
  const collapsed = [...unpinned.slice(0, Math.max(0, maxVisible - pinned.length)), ...pinned]
  const hasOverflow = filters.length > collapsed.length
  const visible = isExpanded ? filters : collapsed
  const hiddenCount = filters.length - visible.length

  return (
    <div className="flex flex-wrap items-center gap-1 px-2 pb-3">
      {visible.map((filter) => (
        <button
          key={filter.id}
          type="button"
          onClick={filter.onRemove}
          aria-label={`${t('resources.generic.remove')} ${filter.label}`}
          className="outline-focus-ring inline-flex h-6.5 max-w-full cursor-pointer items-center gap-1 rounded-full border border-[#D6D3D1] bg-[#F5F5F4] px-2 text-xs leading-none font-medium text-gray-700 hover:bg-[#EDECEA] focus-visible:outline-2 focus-visible:outline-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <span className="truncate">{filter.label}</span>
          <X className="size-3 shrink-0" aria-hidden="true" />
        </button>
      ))}
      {hasOverflow && (
        <button
          type="button"
          onClick={() => setIsExpanded((expanded) => !expanded)}
          aria-label={t(
            isExpanded
              ? 'resources.conversations.inbox.notes.show_less'
              : 'resources.conversations.inbox.notes.show_more'
          )}
          className="outline-focus-ring inline-flex h-6.5 cursor-pointer items-center gap-1 rounded-full border border-[#D6D3D1] bg-[#F5F5F4] px-2 text-xs leading-none font-medium text-gray-700 hover:bg-[#EDECEA] focus-visible:outline-2 focus-visible:outline-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          {isExpanded ? (
            <>
              {filters.length}
              <Minus className="size-3" aria-hidden="true" />
            </>
          ) : (
            `+${hiddenCount}`
          )}
        </button>
      )}
      <button
        type="button"
        onClick={onClearAll}
        className="outline-focus-ring h-6.5 cursor-pointer px-1 text-xs font-medium whitespace-nowrap text-gray-600 hover:text-gray-900 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 dark:text-gray-400 dark:hover:text-gray-100"
      >
        {t('resources.generic.clear_all')}
      </button>
    </div>
  )
}
